import React, { useId, useMemo } from 'react';
import { getCandidate, getCandidateHistory } from '../../utils/jacquesGeometry';
import { dashboardTokens as t } from '../Dashboard/tokens';
import JacquesChartBase from '../JacquesChartBase';

// Grey for the already-traversed segment of the curve (age at reply -> current
// age): distinct from the divider/border greys, muted next to the brand-blue
// projection ahead.
const HISTORY_GREY = '#98A1AB';

// Design size; the SVG scales responsively via viewBox. maxWidth lets the chart
// render larger on screen for better legibility.
const WIDTH = 820;
const HEIGHT = 560;
const MAX_WIDTH = 980;

export default function ReportJacquesChart({ curva, age, ageAtReply, companyChart, language }) {
    return (
        <JacquesChartBase
            width={WIDTH}
            height={HEIGHT}
            maxWidth={MAX_WIDTH}
            companyChart={companyChart}
            language={language}
            bare
        >
            {({ project, innerH, showTip, hideTip }) => (
                <CandidateOverlay
                    curva={curva}
                    age={age}
                    ageAtReply={ageAtReply}
                    project={project}
                    innerH={innerH}
                    showTip={showTip}
                    hideTip={hideTip}
                />
            )}
        </JacquesChartBase>
    );
}

function CandidateOverlay({ curva, age, ageAtReply, project, innerH, showTip, hideTip }) {
    // Unique per instance so multiple charts on one page (e.g. report + dashboard,
    // or the comparison dev page) don't share/collide on these def ids.
    const uid = useId();
    const areaId = `rjc-area-${uid}`;
    const shadowId = `rjc-shadow-${uid}`;
    const candidate = useMemo(() => getCandidate(curva, age, project), [curva, age, project]);
    const history = useMemo(
        () => getCandidateHistory(curva, ageAtReply, age, project),
        [curva, ageAtReply, age, project],
    );

    const areaPath = candidate.points.length
        ? `${candidate.path} L${candidate.points[candidate.points.length - 1].x},${innerH}`
          + ` L${candidate.points[0].x},${innerH} Z`
        : null;

    return (
        <>
            <defs>
                <linearGradient id={areaId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={t.color.primary} stopOpacity={0.18} />
                    <stop offset="100%" stopColor={t.color.primary} stopOpacity={0} />
                </linearGradient>
                <filter id={shadowId} x="-20%" y="-20%" width="140%" height="140%">
                    <feDropShadow dx="0" dy="1" stdDeviation="1.5" floodColor={t.color.primary} floodOpacity="0.25" />
                </filter>
            </defs>

            {areaPath && <path d={areaPath} fill={`url(#${areaId})`} stroke="none" />}
            {history.path && (
                <path
                    d={history.path}
                    fill="none"
                    stroke={HISTORY_GREY}
                    strokeWidth={2.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeDasharray="8 6"
                />
            )}
            {history.start && (
                <circle
                    cx={history.start.x}
                    cy={history.start.y}
                    r={5}
                    fill={HISTORY_GREY}
                    stroke="#fff"
                    strokeWidth={2}
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={(e) => showTip(e, `${ageAtReply} años`)}
                    onMouseMove={(e) => showTip(e, `${ageAtReply} años`)}
                    onMouseLeave={hideTip}
                />
            )}
            {candidate.path && (
                <path
                    d={candidate.path}
                    fill="none"
                    stroke={t.color.primary}
                    strokeWidth={3.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeDasharray="8 6"
                    filter={`url(#${shadowId})`}
                />
            )}
            {candidate.dot && (
                <circle
                    cx={candidate.dot.x}
                    cy={candidate.dot.y}
                    r={7}
                    fill={t.color.primary}
                    stroke="#fff"
                    strokeWidth={3}
                    filter={`url(#${shadowId})`}
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={(e) => showTip(e, `${age} años`)}
                    onMouseMove={(e) => showTip(e, `${age} años`)}
                    onMouseLeave={hideTip}
                />
            )}
        </>
    );
}
